import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Sidebar } from './Sidebar';
import { TopHeader } from './TopHeader';

/**
 * AdminShell Layout Styled Components
 * 
 * Note: These components are shell-specific layout containers and are NOT imported
 * from adminShared.ts because they have unique requirements:
 * - ShellContainer: Top-level flex layout for sidebar + main content
 * - MainArea: Responds to sidebar collapse state with responsive transitions
 * - ContentArea: Shell-specific padding and overflow handling
 * - MobileOverlay: Mobile menu overlay (distinct from ModalOverlay in shared styles)
 * 
 * They represent structural layout unique to the admin shell and should not be
 * generalized or duplicated elsewhere.
 */

const ShellContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: var(--ds-surface-muted, #f5f7fa);
`;

const MainArea = styled.div<{ $sidebarCollapsed: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  transition: margin-left 0.25s ease;

  @media (max-width: 900px) {
    margin-left: 0;
  }
`;

const ContentArea = styled.main`
  flex: 1;
  padding: 24px;
  overflow-y: auto;

  @media (max-width: 600px) {
    padding: 16px;
  }
`;

const MobileOverlay = styled.div<{ $visible: boolean }>`
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: opacity 0.25s ease, visibility 0.25s ease;

  @media (max-width: 900px) {
    display: block;
  }
`;

// Route to title mapping (supports both /admin/* and /t/:slug/* patterns)
const routeTitles: Record<string, string> = {
  '/admin/dashboard': 'Dashboard',
  '/admin/analytics': 'Analytics',
  '/admin/projects': 'Projects',
  '/admin/fiduciaries': 'Data Fiduciaries',
  '/admin/data-collectors': 'Data Fiduciaries',
  '/admin/templates': 'Templates',
  '/admin/consent-forms': 'Generate URL',
  '/admin/generate-url': 'Generate URL',
  '/admin/consents': 'Consent Artifacts',
  '/admin/users': 'User Management',
  '/admin/signup-requests': 'Signup Requests',
  '/admin/tenant-profile': 'Tenant Profile',
  '/admin/user-profile': 'User Profile',
  '/admin/audit-logs': 'Audit Logs',
  '/admin/withdrawals': 'Withdrawal Requests',
  '/admin/help': 'Help & Support',
  '/admin/template-library': 'Template Library',
  '/admin/regions': 'Region Management',
};

// Helper to normalize path (handles both /admin/* and /t/:slug/* patterns)
const normalizePath = (pathname: string): string => {
  // Convert /t/:slug/* to /admin/* for mapping lookup
  const tenantMatch = pathname.match(/^\/t\/[^/]+\/(.*)/);
  if (tenantMatch) {
    return `/admin/${tenantMatch[1]}`;
  }
  return pathname;
};

// Route to breadcrumb mapping
const routeBreadcrumbs: Record<string, { label: string; path?: string }[]> = {
  '/admin/dashboard': [{ label: 'Dashboard' }],
  '/admin/analytics': [{ label: 'Dashboard', path: '/admin/dashboard' }, { label: 'Analytics' }],
  '/admin/projects': [{ label: 'Dashboard', path: '/admin/dashboard' }, { label: 'Projects' }],
  '/admin/fiduciaries': [{ label: 'Dashboard', path: '/admin/dashboard' }, { label: 'Data Fiduciaries' }],
  '/admin/data-collectors': [{ label: 'Dashboard', path: '/admin/dashboard' }, { label: 'Data Fiduciaries' }],
  '/admin/templates': [{ label: 'Dashboard', path: '/admin/dashboard' }, { label: 'Templates' }],
  '/admin/consent-forms': [{ label: 'Dashboard', path: '/admin/dashboard' }, { label: 'Generate URL' }],
  '/admin/generate-url': [{ label: 'Dashboard', path: '/admin/dashboard' }, { label: 'Generate URL' }],
  '/admin/consents': [{ label: 'Dashboard', path: '/admin/dashboard' }, { label: 'Consent Artifacts' }],
  '/admin/users': [{ label: 'Dashboard', path: '/admin/dashboard' }, { label: 'Users' }],
  '/admin/signup-requests': [{ label: 'Dashboard', path: '/admin/dashboard' }, { label: 'Signup Requests' }],
  '/admin/tenant-profile': [{ label: 'Dashboard', path: '/admin/dashboard' }, { label: 'Settings' }, { label: 'Tenant Profile' }],
  '/admin/user-profile': [{ label: 'Dashboard', path: '/admin/dashboard' }, { label: 'Settings' }, { label: 'User Profile' }],
  '/admin/audit-logs': [{ label: 'Dashboard', path: '/admin/dashboard' }, { label: 'Audit Logs' }],
  '/admin/withdrawals': [{ label: 'Dashboard', path: '/admin/dashboard' }, { label: 'Withdrawal Requests' }],
  '/admin/help': [{ label: 'Dashboard', path: '/admin/dashboard' }, { label: 'Help & Support' }],
  '/admin/template-library': [{ label: 'Dashboard', path: '/admin/dashboard' }, { label: 'Template Library' }],
};

interface AdminShellProps {
  children: React.ReactNode;
  title?: string;
}

export const AdminShell: React.FC<AdminShellProps> = ({ children, title }) => {
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Load sidebar state from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    if (saved) {
      setSidebarCollapsed(saved === 'true');
    }
  }, []);

  const handleSidebarCollapse = (collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
    localStorage.setItem('sidebarCollapsed', String(collapsed));
  };

  const normalizedPath = normalizePath(location.pathname);
  const pageTitle = title || routeTitles[normalizedPath] || 'Admin';
  const breadcrumbs = routeBreadcrumbs[normalizedPath];

  return (
    <ShellContainer>
      <MobileOverlay $visible={mobileMenuOpen} onClick={() => setMobileMenuOpen(false)} />
      
      <Sidebar
        collapsed={sidebarCollapsed && !mobileMenuOpen}
        onCollapse={handleSidebarCollapse}
      />

      <MainArea $sidebarCollapsed={sidebarCollapsed}>
        <TopHeader
          title={pageTitle}
          breadcrumbs={breadcrumbs}
          onMenuClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        />

        <ContentArea>
          {children}
        </ContentArea>
      </MainArea>
    </ShellContainer>
  );
};

export default AdminShell;
